import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { Outlet, useMatches, useRouteError } from 'react-router';

import { SkipLink } from '@/components/atoms';
import { ErrorState } from '@/components/molecules';
import {
  FloatingWhatsApp,
  ScrollNudge,
  SiteFooter,
  SiteHeader,
  STICKY_ACTION_BAR_HEIGHT,
  StickyActionBar,
  TOP_BAR_HEIGHT,
} from '@/components/organisms';
import { PROMISE_BAR_HEIGHT_VAR } from '@/theme';

/** A route opts into the floating header with `export const handle = { transparentHeader: true }`. */
export type SiteRouteHandle = { transparentHeader?: boolean };

function useTransparentHeader(): boolean {
  return useMatches().some((m) => (m.handle as SiteRouteHandle | undefined)?.transparentHeader);
}

function Chrome({ children }: { children: React.ReactNode }) {
  const transparent = useTransparentHeader();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SkipLink targetId="main" />
      <SiteHeader transparent={transparent} />
      {/* Opaque header: push content below it. A transparent header overlays the hero. */}
      {!transparent && (
        <Toolbar
          sx={{
            // The ribbon's height comes from a variable rather than a number:
            // it is 40px until the visitor dismisses it and 0 afterwards, and
            // this spacer is what stops the page starting underneath it.
            minHeight: {
              xs: `calc(64px + var(${PROMISE_BAR_HEIGHT_VAR}, 0px))`,
              md: `calc(${64 + TOP_BAR_HEIGHT}px + var(${PROMISE_BAR_HEIGHT_VAR}, 0px))`,
              lg: `calc(${72 + TOP_BAR_HEIGHT}px + var(${PROMISE_BAR_HEIGHT_VAR}, 0px))`,
            },
          }}
          aria-hidden
        />
      )}
      <Box component="main" id="main" tabIndex={-1} sx={{ flex: 1, outline: 'none' }}>
        {children}
      </Box>
      <SiteFooter />
      {/* The sticky bar covers the last strip of the page on phones. */}
      <Box
        sx={{ display: { xs: 'block', md: 'none' }, height: STICKY_ACTION_BAR_HEIGHT }}
        aria-hidden
      />
      <FloatingWhatsApp />
      <StickyActionBar />
      {/* Last, and here rather than in a page, because the tip a visitor sees
          is decided by the route they are on rather than by anything the page
          renders (`config/pageTips.ts`). Mounted once for the whole site: a
          per-page card would be a second one to co-ordinate with the chrome
          above, and two tips on screen at once is the failure mode. */}
      <ScrollNudge />
    </Box>
  );
}

export default function SiteLayout() {
  return (
    <Chrome>
      <Outlet />
    </Chrome>
  );
}

/** Errors inside a page keep the header and footer. */
export function ErrorBoundary() {
  const error = useRouteError();
  return (
    <Chrome>
      <ErrorState error={error} onRetry={() => window.location.reload()} />
    </Chrome>
  );
}
